import { AngleIronPartsArrays } from '@App/app/entities/layer/angle-iron.model';
import { ANGLE_IRON_PART, ANGLE_IRON_STRICT } from '@App/app/entities/layer/enums/layer-types.enum';
import { Injectable } from '@angular/core';
import { AbstractMesh, Mesh, Plane, Vector3 } from 'babylonjs';
import { Subject } from 'rxjs';
import { disposeMeshesInPlace } from '../../../helpers/layers-helpers';
import { BabylonNodesService } from '../../babylon-nodes-service/babylon-nodes.service';
import { AngleIronMeshService } from './angle-iron-mesh.service';

@Injectable({
  providedIn: 'root',
})
export class AngleIronSampleService {
  activePart: ANGLE_IRON_STRICT | null = null;
  highlightedSample: { part: ANGLE_IRON_PART; i: number } = { part: ANGLE_IRON_PART.NONE, i: -1 };
  samples: AngleIronPartsArrays<AbstractMesh | null> = { 1: [], 2: [], 3: [], 4: [] };

  private planes: {
    [ANGLE_IRON_PART.SIDE_1_OUTER]: Plane | null;
    [ANGLE_IRON_PART.SIDE_2_OUTER]: Plane | null;
  } = { 1: null, 3: null };
  private _angleIronDataChange$ = new Subject<void>();
  angleIronDataChange$ = this._angleIronDataChange$.asObservable();

  constructor(
    private nodeService: BabylonNodesService,
    private meshService: AngleIronMeshService,
  ) {}

  createSamplePoint(position: Vector3) {
    const sphere = this.nodeService.createSphere(position);
    if (this.activePart) {
      this.samples[this.activePart].push(sphere);
    }
  }

  deleteSamplePoint(part: ANGLE_IRON_STRICT, i: number) {
    this.samples[part][i]?.dispose();
    this.samples[part].splice(i, 1);
    if (this.highlightedSample.part === part && this.highlightedSample.i === i) {
      this.highlightedSample = { part: ANGLE_IRON_PART.NONE, i: -1 };
    }
    this._angleIronDataChange$.next();
  }

  highlightSample(part: ANGLE_IRON_STRICT, i: number) {
    if (
      this.highlightedSample.part !== ANGLE_IRON_PART.NONE &&
      this.samples[this.highlightedSample.part][this.highlightedSample.i]
    ) {
      this.meshService.changeSampleMaterial(
        this.samples[this.highlightedSample.part][this.highlightedSample.i] as Mesh,
        true,
      );
    }
    if (this.highlightedSample.part === part && this.highlightedSample.i === i) {
      this.highlightedSample = { part: ANGLE_IRON_PART.NONE, i: -1 };
    } else {
      this.meshService.changeSampleMaterial(this.samples[part][i] as Mesh, false);
      this.highlightedSample = { part, i };
    }
    this._angleIronDataChange$.next();
  }

  setActivePart(part: ANGLE_IRON_STRICT) {
    this.activePart = this.activePart === part ? null : part;
    this._angleIronDataChange$.next();
  }

  buildPlanesFromSamples(): { planes: [Plane, Plane]; innerSamples: [Mesh, Mesh] } {
    const z = Vector3.Zero();
    const sidePoints1 = this.samples[ANGLE_IRON_PART.SIDE_1_OUTER].map(
      (mesh) => mesh?.position || z,
    );
    const sidePoints2 = this.samples[ANGLE_IRON_PART.SIDE_2_OUTER].map(
      (mesh) => mesh?.position || z,
    );
    this.planes[ANGLE_IRON_PART.SIDE_1_OUTER] = Plane.FromPoints(
      sidePoints1[0],
      sidePoints1[1],
      sidePoints1[2],
    );
    this.planes[ANGLE_IRON_PART.SIDE_2_OUTER] = Plane.FromPoints(
      sidePoints2[0],
      sidePoints2[1],
      sidePoints2[2],
    );
    return {
      planes: [
        this.planes[ANGLE_IRON_PART.SIDE_1_OUTER] as Plane,
        this.planes[ANGLE_IRON_PART.SIDE_2_OUTER] as Plane,
      ],
      innerSamples: [
        this.samples[ANGLE_IRON_PART.SIDE_1_INNER][0] as Mesh,
        this.samples[ANGLE_IRON_PART.SIDE_2_INNER][0] as Mesh,
      ],
    };
  }

  notifyChange() {
    this._angleIronDataChange$.next();
  }

  clear() {
    this.activePart = null;
    for (const part of (Object.keys(this.samples) as unknown) as ANGLE_IRON_STRICT[]) {
      this.removeSamplesOfPart(part);
    }
    this.samples = { 1: [], 2: [], 3: [], 4: [] };
    this.planes = { 1: null, 3: null };
    this.highlightedSample = { part: ANGLE_IRON_PART.NONE, i: -1 };
    this._angleIronDataChange$.next();
  }

  private removeSamplesOfPart(part: ANGLE_IRON_STRICT) {
    disposeMeshesInPlace(this.samples[part]);
  }
}
